import type {} from '@/types/snackbar';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { type CustomContentProps, SnackbarContent, useSnackbar } from 'notistack';
import * as React from 'react';

import { useSnackbarLifetime } from '@/hooks/useSnackbarLifetime';
import { IconCircleCheck } from '@/components/icon/tabler/IconCircleCheck';
import { IconCircleX } from '@/components/icon/tabler/IconCircleX';
import { IconInfoCircle } from '@/components/icon/tabler/IconInfoCircle';
import { IconAlertTriangle } from '@/components/icon/tabler/IconAlertTriangle';
import { controlTokens, gray, green, orange, surfaceTokens } from '@/theme/themePrimitives';

export const SnackbarAlert = React.forwardRef<HTMLDivElement, CustomContentProps>(
  function SnackbarAlert(
    { message, variant, id, className, style, action, durationMs = 5000, persist },
    ref,
  ) {
    const { closeSnackbar } = useSnackbar();
    const isError = variant === 'error';
    const Icon =
      variant === 'success'
        ? IconCircleCheck
        : isError
          ? IconCircleX
          : variant === 'warning'
            ? IconAlertTriangle
            : IconInfoCircle;
    const statusColor =
      variant === 'success'
        ? green[300]
        : isError
          ? controlTokens.error
          : variant === 'warning'
            ? orange[300]
            : controlTokens.infoFillHover;
    const hasTimer = !persist && durationMs !== null && durationMs > 0;
    const barRef = useSnackbarLifetime(hasTimer ? durationMs : null, () => closeSnackbar(id));

    const handleClick = React.useCallback(() => {
      closeSnackbar(id);
    }, [closeSnackbar, id]);

    return (
      <SnackbarContent
        ref={ref}
        role="alert"
        data-testid="snackbar-alert"
        id={id as string}
        className={className}
        style={style}
      >
        <Box sx={sx.root(isError)} onClick={handleClick}>
          <Icon
            sx={{
              fontSize: 20,
              flexShrink: 0,
              color: statusColor,
              '& path, & circle, & polyline, & line': { strokeWidth: 2.5 },
            }}
          />
          <Typography variant="body2" sx={sx.message(isError)}>
            {message}
          </Typography>
          {action ? (
            // The action decides whether the snackbar closes; a click on it must not
            // fall through to the dismiss-on-click of the whole alert.
            <Box sx={sx.action(isError)} onClick={(event) => event.stopPropagation()}>
              {typeof action === 'function' ? action(id) : action}
            </Box>
          ) : null}
          {hasTimer ? (
            <Box
              ref={barRef}
              data-testid="snackbar-timer"
              aria-hidden
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 3,
                bgcolor: isError ? controlTokens.error : surfaceTokens.coral,
                transformOrigin: 'left center',
              }}
            />
          ) : null}
        </Box>
      </SnackbarContent>
    );
  },
);

const sx = {
  root: (isError: boolean) => ({
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    minHeight: 52,
    px: '18px',
    py: '8px',
    cursor: 'default',
    borderRadius: '16px',
    width: 'max-content',
    maxWidth: 'min(560px, 100%)',
    boxSizing: 'border-box',
    mx: 'auto',
    boxShadow: surfaceTokens.shadowOverlay,
    backgroundColor: isError ? surfaceTokens.paper : controlTokens.primary,
    color: isError ? gray[800] : surfaceTokens.paper,
  }),
  message: (isError: boolean) => ({
    color: isError ? gray[800] : surfaceTokens.paper,
    minWidth: 0,
    overflowWrap: 'anywhere',
    fontWeight: 500,
    fontSize: '15px',
    lineHeight: '20.25px',
  }),
  action: (isError: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    '& .MuiButton-root': {
      color: isError ? controlTokens.cobalt : '#FF8A84',
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '14px',
      minWidth: 0,
      px: '12px',
      py: '10px',
      height: 34,
      borderRadius: '10px',
      '&:hover': {
        backgroundColor: isError ? 'rgba(74, 0, 226, 0.06)' : 'rgba(255, 255, 255, 0.08)',
      },
    },
  }),
};
